import { useMemo } from 'react';
import type {
  ConnectionStatus,
  SDKCaptureResult,
  CaptureMode,
  StreamModality,
  VideoFrame,
  CaptureProgress,
  PowerComponent,
  PowerState,
  DeviceInfo,
} from '@ethbridge/core';
import {
  useBridgeContext,
  useBridgeSelector,
  type CaptureState,
  type CaptureOptions,
} from '../context/BridgeContext';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Return type for useBridge hook
 */
export interface UseBridgeReturn {
  // Connection
  /** Current connection status */
  status: ConnectionStatus;

  /** Whether connected to Iron Bridge */
  isConnected: boolean;

  /** Connect to Iron Bridge */
  connect: () => Promise<void>;

  /** Disconnect from Iron Bridge */
  disconnect: () => Promise<void>;

  /** Reconnect to Iron Bridge */
  reconnect: () => Promise<void>;

  // Capture
  /** Current capture status */
  captureStatus: CaptureState['status'];

  /** Capture result data */
  captureData: SDKCaptureResult | null;

  /** Capture error */
  captureError: Error | null;

  /** Capture progress */
  captureProgress: CaptureProgress | null;

  /** Start a capture */
  capture: (mode: CaptureMode, options?: CaptureOptions) => void;

  /** Start a capture (async) */
  captureAsync: (mode: CaptureMode, options?: CaptureOptions) => Promise<SDKCaptureResult>;

  /** Cancel current capture */
  cancelCapture: () => void;

  // Video
  /** Video source URL (data URL) */
  videoSrc: string | null;

  /** Whether streaming is active */
  isStreaming: boolean;

  /** Video frame count */
  frameCount: number;

  /** Last video frame */
  lastFrame: VideoFrame | null;

  /** Start video stream */
  startStream: (modality: StreamModality, options?: { autoCapture?: boolean }) => void;

  /** Stop video stream */
  stopStream: () => void;

  // Power
  /** Current power state */
  powerState: PowerState | null;

  /** Power on component */
  powerOn: (component?: PowerComponent) => void;

  /** Power off component */
  powerOff: (component?: PowerComponent) => void;

  // Devices
  /** Connected devices */
  devices: DeviceInfo[];

  /** Refresh devices list */
  refreshDevices: () => Promise<DeviceInfo[]>;

  // Commands
  /** Send command (fire and forget) */
  sendCommand: (action: string, params?: Record<string, unknown>) => void;

  /** Send command and wait for response */
  sendCommandAsync: <T = unknown>(
    action: string,
    params?: Record<string, unknown>,
    timeoutMs?: number
  ) => Promise<T>;

  // Errors
  /** Last error */
  lastError: Error | null;

  // Low-level
  /** Subscribe to store changes */
  subscribe: (listener: () => void) => () => void;
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Main Bridge hook - provides access to all Bridge functionality
 *
 * @example
 * ```tsx
 * function CaptureButton() {
 *   const {
 *     isConnected,
 *     connect,
 *     capture,
 *     captureData,
 *     captureStatus,
 *   } = useBridge();
 *
 *   useEffect(() => {
 *     if (!isConnected) {
 *       connect();
 *     }
 *   }, [isConnected, connect]);
 *
 *   return (
 *     <button
 *       onClick={() => capture('fingerprint')}
 *       disabled={!isConnected || captureStatus === 'capturing'}
 *     >
 *       {captureStatus === 'capturing' ? 'Capturing...' : 'Capture Fingerprint'}
 *     </button>
 *   );
 * }
 * ```
 */
export function useBridge(): UseBridgeReturn {
  const context = useBridgeContext();

  // Connection state
  const status = useBridgeSelector((state) => state.connectionStatus);
  const isConnected = useBridgeSelector((state) => state.isConnected);

  // Capture state
  const captureStatus = useBridgeSelector((state) => state.capture.status);
  const captureData = useBridgeSelector((state) => state.capture.data);
  const captureError = useBridgeSelector((state) => state.capture.error);
  const captureProgress = useBridgeSelector((state) => state.capture.progress);

  // Video state
  const videoSrc = useBridgeSelector((state) => state.video.src);
  const isStreaming = useBridgeSelector((state) => state.video.isStreaming);
  const frameCount = useBridgeSelector((state) => state.video.frameCount);
  const lastFrame = useBridgeSelector((state) => state.video.lastFrame);

  // Power state
  const powerState = useBridgeSelector((state) => state.powerState);

  // Devices
  const devices = useBridgeSelector((state) => state.devices);

  // Errors
  const lastError = useBridgeSelector((state) => state.lastError);

  return useMemo(
    () => ({
      // Connection
      status,
      isConnected,
      connect: context.connect,
      disconnect: context.disconnect,
      reconnect: context.reconnect,

      // Capture
      captureStatus,
      captureData,
      captureError,
      captureProgress,
      capture: context.capture,
      captureAsync: context.captureAsync,
      cancelCapture: context.cancelCapture,

      // Video
      videoSrc,
      isStreaming,
      frameCount,
      lastFrame,
      startStream: context.startStream,
      stopStream: context.stopStream,

      // Power
      powerState,
      powerOn: context.powerOn,
      powerOff: context.powerOff,

      // Devices
      devices,
      refreshDevices: context.refreshDevices,

      // Commands
      sendCommand: context.sendCommand,
      sendCommandAsync: context.sendCommandAsync,

      // Errors
      lastError,

      // Low-level
      subscribe: context.store.subscribe,
    }),
    [
      status,
      isConnected,
      captureStatus,
      captureData,
      captureError,
      captureProgress,
      videoSrc,
      isStreaming,
      frameCount,
      lastFrame,
      powerState,
      devices,
      lastError,
      context,
    ]
  );
}
